import { Suspense, lazy, type ComponentType, type JSX } from 'react';

import { chunkReloader } from '@/lib/chunkReload';
import { ChunkReloadNotice, RouteLoading } from '@/lib/RouteLoading';

type JSX_IntrinsicAttributes = JSX.IntrinsicAttributes;

/**
 * Story `fe-be-monteur-s5b-installeerbaar` (EPIC-021, S021.5b) deel A — een scherm dat pas
 * geladen wordt als zijn route opent (G-1: de kleine bundel voor `/monteur`).
 *
 * <b>Waarom `React.lazy` per element en niet de `lazy`-eigenschap van de route</b> (§1.1,
 * keuze van de developer): de routeboom blijft zo letterlijk gelijk (`element: <X />`), en
 * de guards blijven vóór het scherm beslissen. Met route-`lazy` laadt de router de module
 * van élke gematchte route vóór hij rendert — dan haalt een gebruiker die een
 * `<ProtectedRoute>` weigert het scherm tóch op (TC-FE-REG-08). Met `React.lazy` rendert de
 * guard eerst; een `<Navigate>` laat het scherm nooit ophalen.
 *
 * <b>Laadstaat</b> (§1.1): dezelfde "Laden…" als `FitterShellRoute` vóór deze story.
 *
 * <b>Een bestand dat na een deploy weg is</b> (§1.2): de afwijzing van de import gaat naar
 * `chunkReloader()` — één automatische herlaad, daarna de melding met een knop.
 *
 * @param loader de `import()` van de module (een letterlijke specifier, zodat Vite splitst)
 * @param name   de export van die module die het scherm is; ook de naam van het resultaat
 *               (de routetests herkennen `<Chrome>` aan zijn naam)
 * @param fullScreen `true` voor een schil (Chrome, de monteur-schil): de laadstaat vult het
 *               scherm; anders vult hij het inhoudsvlak binnen de schil
 */
export function lazyScreen<M, K extends keyof M & string>(
    loader: () => Promise<M>,
    name: K,
    fullScreen = false,
): LazyScreen<PropsOf<M[K]>> {
    type P = PropsOf<M[K]>;
    const key = name;

    const Lazy = lazy(async (): Promise<{ default: ComponentType<P> }> => {
        try {
            const mod = await loader();
            chunkReloader().onSuccess(key);
            return { default: mod[name] as unknown as ComponentType<P> };
        } catch {
            // Een laadfout (geen renderfout — die komt hier niet langs, TC-FE-CHK-08).
            if (chunkReloader().onFailure(key) === 'reload') {
                // De pagina herlaadt; tot die tijd de laadstaat (nooit afronden).
                return new Promise<never>(() => {});
            }
            return { default: ChunkReloadNotice as unknown as ComponentType<P> };
        }
    });

    function Screen(props: P) {
        return (
            <Suspense fallback={<RouteLoading fullScreen={fullScreen} />}>
                <Lazy {...(props as P & JSX_IntrinsicAttributes)} />
            </Suspense>
        );
    }
    // De naam van het scherm: `GRD-06` herkent de kantoorschil aan `type.name === 'Chrome'`.
    Object.defineProperty(Screen, 'name', { value: name });
    // Voor de route-smoketest (TC-FE-REG-02): wijst elke lazy route naar een bestaande export?
    const screen = Screen as LazyScreen<P>;
    screen.load = async () => (await loader())[name];
    return screen;
}

/** Een lazy scherm: een component plus `load()`, dat de export zelf ophaalt (voor de smoketest). */
export type LazyScreen<P> = ComponentType<P> & { load: () => Promise<unknown> };

/** De props van een component-type (of `object` als het geen component is). */
type PropsOf<T> = T extends ComponentType<infer P> ? P : object;
